import { useMutation, useQuery } from "convex/react";
import { MoreHorizontal } from "lucide-react";
import { useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router";

import { DeckCards } from "@/components/DeckCards";
import { DeckExtraReviewMessage } from "@/components/DeckExtraReviewMessage";
import { DeckReviewMessage } from "@/components/DeckReviewMessage";
import { ErrorMessage } from "@/components/ErrorMessage";
import { LazySpinner } from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAllDecks } from "@/hooks/useAllDecks";
import { useReviewTime } from "@/hooks/useReviewTime";
import { api } from "@convex/_generated/api";

export const Deck = () => {
  const { deckId: maybeDeckId } = useParams();
  const reviewTime = useReviewTime();

  const decks = useAllDecks();
  const currentDeck = decks?.find((deck) => deck._id === maybeDeckId);
  const deckId = currentDeck?._id;
  const name = currentDeck?.name;
  const isMuted = currentDeck?.isMuted ?? false;
  const cardCount = currentDeck?.cardCount ?? 0;
  const toReviewCount = currentDeck?.toReviewCount ?? 0;

  const cards = useQuery(api.cards.getByDeck, deckId ? { deckId } : "skip");

  // Deck name update handlers.
  // TODO: this will spam the server with updates if the user types quickly. We should find a way to debounce it.
  const updateNameMutation = useMutation(api.decks.updateName).withOptimisticUpdate(
    (localStore, { deckId, name }) => {
      const allDecks = localStore.getQuery(api.decks.getAll, { reviewTime }) ?? [];
      const currentDeckIndex = allDecks.findIndex((d) => d._id === deckId);
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- we just used findIndex to get a real index
      const currentDeck = allDecks[currentDeckIndex]!;
      if (currentDeckIndex !== -1) {
        const update = {
          ...currentDeck,
          name,
        };
        const updatedDecks = [...allDecks];
        updatedDecks.splice(currentDeckIndex, 1, update);
        localStore.setQuery(api.decks.getAll, { reviewTime }, updatedDecks);
      }
    }
  );
  const handleUpdateName = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (deckId) {
        void updateNameMutation({ deckId, name: event.target.value });
      }
    },
    [deckId, updateNameMutation]
  );

  // "Delete deck" button handlers.
  const deleteDeck = useMutation(api.decks.deleteDeck);
  const navigate = useNavigate();
  const handleDeleteDeck = useCallback(async () => {
    if (deckId) {
      await deleteDeck({ deckId });
      void navigate("/"); // Redirect to the home page after deletion
    }
  }, [deckId, deleteDeck, navigate]);

  // "Rename deck" button handlers.
  const deckTitleRef = useRef<HTMLTextAreaElement>(null);
  const shouldFocusDeckTitleRef = useRef(false);
  const handleRenameButtonClick = useCallback(() => {
    shouldFocusDeckTitleRef.current = true;
  }, []);
  const handleOnCloseAutoFocus = useCallback((e: Event) => {
    if (shouldFocusDeckTitleRef.current) {
      e.preventDefault();
      deckTitleRef.current?.focus();
      const length = deckTitleRef.current?.value.length;
      if (length) {
        deckTitleRef.current?.setSelectionRange(length, length);
      }
      shouldFocusDeckTitleRef.current = false;
    }
  }, []);

  // "Mute deck" button handlers.
  const updateMuteStatus = useMutation(api.decks.updateMuteStatus);
  const handleMuteDeck = useCallback(async () => {
    if (deckId) {
      await updateMuteStatus({ deckId, isMuted: !isMuted });
    }
  }, [updateMuteStatus, deckId, isMuted]);

  if (!decks) {
    return (
      <div className="h-full flex items-center justify-center">
        <LazySpinner />
      </div>
    );
  }

  if (!deckId) {
    return (
      <ErrorMessage
        title="Could not find deck"
        message="The deck you're looking for might have been deleted or doesn't exist."
      />
    );
  }

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <textarea
          value={name}
          ref={deckTitleRef}
          onChange={handleUpdateName}
          title="Deck name"
          placeholder="Deck name"
          className="text-2xl font-bold w-full resize-none outline-none bg-transparent border border-transparent hover:border-gray-200 rounded transition-all -ml-2 pl-2 py-1"
          style={{ overflow: "hidden", cursor: "text" }}
          rows={1}
        />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <MoreHorizontal className="h-5 w-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onCloseAutoFocus={handleOnCloseAutoFocus}>
            <DropdownMenuItem onClick={handleRenameButtonClick}>Rename deck</DropdownMenuItem>
            <DropdownMenuItem onClick={handleMuteDeck}>
              {isMuted ? "Unmute deck notifications" : "Mute deck notifications"}
            </DropdownMenuItem>
            <DropdownMenuItem variant="destructive" onClick={handleDeleteDeck}>
              Delete deck
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {toReviewCount > 0 ? (
        <DeckReviewMessage toReviewCount={toReviewCount} deckId={deckId} />
      ) : cardCount > 0 ? (
        <DeckExtraReviewMessage cardCount={cardCount} deckId={deckId} />
      ) : null}

      <DeckCards cards={cards} deckId={deckId} cardCount={cardCount} key={deckId} />
    </>
  );
};
